import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    FaTimes,
    FaEnvelope,
    FaLock,
    FaUser,
    FaArrowRight,
    FaShieldAlt,
    FaCheckCircle,
} from "react-icons/fa";

export default function LoginModal({ isOpen, onClose, setUser }) {
    const [mode, setMode] = useState("login");
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
    });

    if (!isOpen) return null;

    // HANDLE INPUT CHANGE
    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });

        setErrors({
            ...errors,
            [e.target.name]: "",
            general: "",
        });

        setSuccess("");
    };

    // VALIDATION
    const validate = () => {
        let newErrors = {};

        if (mode === "register" && !form.name.trim()) {
            newErrors.name = "Full name is required";
        }

        if (!form.email.trim()) {
            newErrors.email = "Email is required";
        }

        if (mode !== "forgot" && !form.password.trim()) {
            newErrors.password = "Password is required";
        }

        return newErrors;
    };

    // SUBMIT
    const handleSubmit = async (e) => {
        e.preventDefault();

        setErrors({});
        setSuccess("");

        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {
            // FORGOT PASSWORD
            if (mode === "forgot") {
                await axios.post(
                    "https://beachhoteldubai.com/api/forgot-password",
                    {
                        email: form.email,
                    }
                );

                setSuccess(
                    "If an account exists with this email, a password reset link has been sent."
                );

                setLoading(false);
                return;
            }

            // REGISTER
            if (mode === "register") {
                await axios.post(
                    "https://beachhoteldubai.com/api/register",
                    {
                        name: form.name,
                        email: form.email,
                        password: form.password,
                        password_confirmation: form.password,
                    }
                );

                alert("User registered successfully");

                setForm({
                    name: "",
                    email: "",
                    password: "",
                });

                setMode("login");
                setLoading(false);

                return;
            }

            // LOGIN
            if (mode === "login") {
                const res = await axios.post(
                    "https://beachhoteldubai.com/api/login",
                    {
                        email: form.email,
                        password: form.password,
                    }
                );

                const userData = res.data.user;

                // Save user
                localStorage.setItem(
                    "user",
                    JSON.stringify(userData)
                );

                setUser(userData);

                alert("Login successful");

                setLoading(false);

                // Close modal
                onClose();

                // Redirect
                navigate("/profile");
            }
        } catch (err) {
            console.error(err);

            setLoading(false);

            if (mode === "forgot") {
                setErrors({
                    general:
                        err.response?.data?.message ||
                        "Unable to send the reset link. Please try again later.",
                });
            } else {
                setErrors({
                    general:
                        err.response?.data?.message ||
                        "Authentication failed. Please check your details.",
                });
            }
        }
    };

    // SWITCH TO LOGIN
    const goToLogin = () => {
        setMode("login");
        setErrors({});
        setSuccess("");

        setForm({
            name: "",
            email: form.email,
            password: "",
        });
    };

    // SWITCH TO REGISTER
    const goToRegister = () => {
        setMode("register");
        setErrors({});
        setSuccess("");
    };

    // SWITCH TO FORGOT PASSWORD
    const goToForgotPassword = () => {
        setMode("forgot");
        setErrors({});
        setSuccess("");

        setForm({
            name: "",
            email: form.email,
            password: "",
        });
    };

    const inputClass = `
        w-full
        rounded-xl
        border
        border-[#E5E8E8]
        bg-[#F8FAFA]
        py-3.5
        pl-11
        pr-4
        text-sm
        font-medium
        text-[#172B35]
        outline-none
        transition-all
        duration-300
        placeholder:text-[#9AA5AA]
        focus:border-[#8B0000]
        focus:bg-white
        focus:ring-4
        focus:ring-[#8B0000]/[0.07]
    `;

    return (
        <div
            className="
                fixed inset-0 z-50
                flex items-center justify-center
                overflow-y-auto
                bg-[#172B35]/75
                px-4 py-6
                backdrop-blur-md
            "
            onClick={onClose}
        >

            {/* MODAL */}
            <div
                className="
                    relative
                    my-auto
                    w-full
                    max-w-[470px]
                    overflow-hidden
                    rounded-[26px]
                    border
                    border-[#E8ECEC]
                    bg-white
                    shadow-[0_30px_100px_rgba(23,43,53,0.30)]
                "
                onClick={(e) => e.stopPropagation()}
            >

                {/* TOP ACCENT */}
                <div className="h-1.5 w-full bg-gradient-to-r from-[#8B0000] via-[#8B0000] to-[#D7A74A]" />

                {/* DECORATIVE BACKGROUND */}
                <div
                    className="
                        pointer-events-none
                        absolute
                        -right-24 -top-24
                        h-56 w-56
                        rounded-full
                        bg-[#8B0000]/[0.035]
                        blur-3xl
                    "
                />

                <div
                    className="
                        pointer-events-none
                        absolute
                        -bottom-24 -left-24
                        h-56 w-56
                        rounded-full
                        bg-[#D7A74A]/[0.10]
                        blur-3xl
                    "
                />

                {/* CLOSE BUTTON */}
                <button
                    onClick={onClose}
                    type="button"
                    aria-label="Close"
                    className="
                        absolute right-5 top-5 z-30
                        flex h-10 w-10
                        items-center justify-center
                        rounded-full
                        border border-[#E8ECEC]
                        bg-white
                        text-[#172B35]
                        shadow-sm
                        transition-all duration-300
                        hover:border-[#8B0000]/30
                        hover:bg-[#8B0000]
                        hover:text-white
                        hover:rotate-90
                    "
                >
                    <FaTimes className="text-sm" />
                </button>

                {/* MAIN CONTENT */}
                <div className="relative z-10 p-6 sm:p-9">

                    {/* BRAND / ICON */}
                    <div className="text-center">

                        <div
                            className="
                                relative mx-auto
                                flex h-16 w-16
                                items-center justify-center
                                rounded-[20px]
                                border border-[#8B0000]/10
                                bg-[#8B0000]/[0.05]
                                text-[#8B0000]
                                shadow-[0_8px_25px_rgba(139,0,0,0.06)]
                            "
                        >
                            <span
                                className="
                                    absolute -right-1 -top-1
                                    h-3 w-3
                                    rounded-full
                                    bg-[#D7A74A]
                                "
                            />

                            {mode === "register" ? (
                                <FaUser className="text-xl" />
                            ) : mode === "forgot" ? (
                                <FaEnvelope className="text-xl" />
                            ) : (
                                <FaLock className="text-xl" />
                            )}
                        </div>

                        {/* EYEBROW */}
                        <div
                            className="
                                mx-auto mt-5
                                inline-flex items-center gap-2
                                text-[10px]
                                font-extrabold
                                uppercase
                                tracking-[0.2em]
                                text-[#8B0000]
                            "
                        >
                            <span className="h-px w-6 bg-[#D7A74A]" />

                            {mode === "register"
                                ? "Join Dubai Marina"
                                : mode === "forgot"
                                    ? "Account Recovery"
                                    : "Welcome Back"}

                            <span className="h-px w-6 bg-[#D7A74A]" />
                        </div>

                        {/* TITLE */}
                        <h2
                            className="
                                mt-4
                                text-3xl
                                font-extrabold
                                tracking-tight
                                text-[#172B35]
                                sm:text-[34px]
                            "
                        >
                            {mode === "register"
                                ? "Create Your Account"
                                : mode === "forgot"
                                    ? "Reset Your Password"
                                    : "Sign In"}
                        </h2>

                        {/* SUBTITLE */}
                        <p
                            className="
                                mx-auto mt-2
                                max-w-sm
                                text-sm
                                leading-6
                                text-[#71808A]
                            "
                        >
                            {mode === "register"
                                ? "Create your account and make managing your Dubai stays easier."
                                : mode === "forgot"
                                    ? "Enter your email and we'll send you a secure password reset link."
                                    : "Sign in to manage your hotel bookings and reservations."}
                        </p>

                    </div>

                    {/* GENERAL ERROR */}
                    {errors.general && (
                        <div
                            className="
                                mt-6 flex items-start gap-3
                                rounded-xl
                                border border-red-200
                                bg-red-50
                                px-4 py-3.5
                                text-sm leading-5
                                text-red-600
                            "
                        >
                            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-red-500" />

                            <span>{errors.general}</span>
                        </div>
                    )}

                    {/* SUCCESS MESSAGE */}
                    {success && (
                        <div
                            className="
                                mt-6 flex items-start gap-3
                                rounded-xl
                                border border-emerald-200
                                bg-emerald-50
                                px-4 py-3.5
                                text-sm leading-5
                                text-emerald-700
                            "
                        >
                            <FaCheckCircle className="mt-0.5 shrink-0" />

                            <span>{success}</span>
                        </div>
                    )}

                    {/* FORM */}
                    <form
                        onSubmit={handleSubmit}
                        className="mt-7 space-y-5"
                    >

                        {/* NAME */}
                        {mode === "register" && (
                            <div>

                                <label
                                    htmlFor="name"
                                    className="
                                        mb-2 block
                                        text-[11px]
                                        font-extrabold
                                        uppercase
                                        tracking-[0.12em]
                                        text-[#344B56]
                                    "
                                >
                                    Full Name
                                </label>

                                <div className="relative">

                                    <FaUser
                                        className="
                                            absolute left-4 top-1/2
                                            -translate-y-1/2
                                            text-xs
                                            text-[#8B0000]/60
                                        "
                                    />

                                    <input
                                        id="name"
                                        name="name"
                                        value={form.name}
                                        placeholder="Enter your full name"
                                        onChange={handleChange}
                                        className={inputClass}
                                    />

                                </div>

                                {errors.name && (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        {errors.name}
                                    </p>
                                )}

                            </div>
                        )}

                        {/* EMAIL */}
                        <div>

                            <label
                                htmlFor="email"
                                className="
                                    mb-2 block
                                    text-[11px]
                                    font-extrabold
                                    uppercase
                                    tracking-[0.12em]
                                    text-[#344B56]
                                "
                            >
                                Email Address
                            </label>

                            <div className="relative">

                                <FaEnvelope
                                    className="
                                        absolute left-4 top-1/2
                                        -translate-y-1/2
                                        text-xs
                                        text-[#8B0000]/60
                                    "
                                />

                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    placeholder="Enter your email"
                                    onChange={handleChange}
                                    className={inputClass}
                                />

                            </div>

                            {errors.email && (
                                <p className="mt-1.5 text-xs text-red-600">
                                    {errors.email}
                                </p>
                            )}

                        </div>

                        {/* PASSWORD */}
                        {mode !== "forgot" && (
                            <div>

                                <label
                                    htmlFor="password"
                                    className="
                                        mb-2 block
                                        text-[11px]
                                        font-extrabold
                                        uppercase
                                        tracking-[0.12em]
                                        text-[#344B56]
                                    "
                                >
                                    Password
                                </label>

                                <div className="relative">

                                    <FaLock
                                        className="
                                            absolute left-4 top-1/2
                                            -translate-y-1/2
                                            text-xs
                                            text-[#8B0000]/60
                                        "
                                    />

                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        value={form.password}
                                        placeholder="Enter your password"
                                        onChange={handleChange}
                                        className={inputClass}
                                    />

                                </div>

                                {errors.password && (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        {errors.password}
                                    </p>
                                )}

                                {/* FORGOT PASSWORD */}
                                {mode === "login" && (
                                    <div className="mt-3 flex justify-end">

                                        <button
                                            type="button"
                                            onClick={goToForgotPassword}
                                            className="
                                                text-xs
                                                font-bold
                                                text-[#8B0000]
                                                transition
                                                hover:text-[#690000]
                                            "
                                        >
                                            Forgot Password?
                                        </button>

                                    </div>
                                )}

                            </div>
                        )}

                        {/* SUBMIT BUTTON */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="
                                group relative
                                flex w-full
                                items-center justify-center gap-3
                                overflow-hidden
                                rounded-xl
                                bg-[#8B0000]
                                px-5 py-4
                                text-sm font-extrabold
                                text-white
                                shadow-[0_12px_30px_rgba(139,0,0,0.16)]
                                transition-all duration-300
                                hover:-translate-y-0.5
                                hover:bg-[#690000]
                                hover:shadow-[0_16px_35px_rgba(139,0,0,0.22)]
                                disabled:cursor-not-allowed
                                disabled:opacity-60
                            "
                        >

                            {/* GOLD HOVER LINE */}
                            <span
                                className="
                                    absolute bottom-0 left-0
                                    h-1 w-0
                                    bg-[#D7A74A]
                                    transition-all duration-500
                                    group-hover:w-full
                                "
                            />

                            {loading ? (
                                <>
                                    <span
                                        className="
                                            h-5 w-5
                                            animate-spin
                                            rounded-full
                                            border-2
                                            border-white/30
                                            border-t-white
                                        "
                                    />

                                    Please wait...
                                </>
                            ) : (
                                <>
                                    {mode === "register"
                                        ? "Create Account"
                                        : mode === "forgot"
                                            ? "Send Reset Link"
                                            : "Sign In"}

                                    <FaArrowRight
                                        className="
                                            text-xs
                                            transition-transform
                                            duration-300
                                            group-hover:translate-x-1
                                        "
                                    />
                                </>
                            )}

                        </button>

                    </form>

                    {/* BOTTOM NAVIGATION */}
                    <div className="mt-6 text-center text-sm text-[#71808A]">

                        {mode === "login" && (
                            <>
                                Don't have an account?{" "}

                                <button
                                    type="button"
                                    onClick={goToRegister}
                                    className="
                                        font-extrabold
                                        text-[#8B0000]
                                        transition
                                        hover:text-[#690000]
                                    "
                                >
                                    Create account
                                </button>
                            </>
                        )}

                        {mode === "register" && (
                            <>
                                Already have an account?{" "}

                                <button
                                    type="button"
                                    onClick={goToLogin}
                                    className="
                                        font-extrabold
                                        text-[#8B0000]
                                        transition
                                        hover:text-[#690000]
                                    "
                                >
                                    Sign in
                                </button>
                            </>
                        )}

                        {mode === "forgot" && (
                            <button
                                type="button"
                                onClick={goToLogin}
                                className="
                                    inline-flex items-center gap-2
                                    font-extrabold
                                    text-[#8B0000]
                                    transition
                                    hover:text-[#690000]
                                "
                            >
                                ← Back to login
                            </button>
                        )}

                    </div>

                    {/* SECURITY NOTE */}
                    <div
                        className="
                            mt-7 flex items-center justify-center gap-2
                            border-t border-[#E8ECEC]
                            pt-5
                            text-[10px]
                            font-extrabold
                            uppercase
                            tracking-[0.15em]
                            text-[#8A969C]
                        "
                    >
                        <FaShieldAlt className="text-[#D7A74A]" />

                        Secure & Protected
                    </div>

                </div>

            </div>

        </div>
    );
}